import { Control } from '@dat/iq-controls';
import { get, isArray, set } from 'lodash';
import { Component, Dashboard, Section } from '../../models';

interface IController {
    build(): Component | Section | Dashboard | Control;
}

export class ControlController implements IController {
    private _control: any;

    constructor(control: Control) {
        this._control = control;
    }

    updateControl(control: Control): this {
        this._control = {
            ...this._control,
            ...control,
        };

        return this;
    }

    build(): Control {
        return this._control;
    }
}

export class PropsController {
    private _props: any = {};
    private _controls: Map<string, ControlController> = new Map<
        string,
        ControlController
    >();
    private _controlOrder: string[] = [];

    constructor(props: any) {
        this.setProps(props);
    }

    updateControlById(controlId: string, control: any): this {
        if (!this._controls.has(controlId)) return this;

        this._controls.get(controlId)?.updateControl(control);

        return this;
    }

    setProps(props: any): this {
        this._props = props;

        return this.setControls(props.controls ?? []);
    }

    update(newProps: any = {}): this {
        this._props = {
            ...this._props,
            ...newProps,
        };

        if (!!newProps.controls?.length) this.setControls(newProps.controls);

        return this;
    }

    updateControl(controlId: string, control: Partial<Control>) {
        this._controls.get(controlId)?.updateControl(control);
    }

    setControls(controls: Control[] = []): this {
        this._controls = new Map<string, ControlController>();
        this._controlOrder = [...controls.map((control) => control.id)];

        controls.forEach((control) => {
            this._controls.set(control.id, new ControlController(control));
        });

        return this;
    }

    build(): any {
        return !!this._controls?.size ? this._buildControls() : this._props;
    }

    private _buildControls(): any {
        return {
            ...this._props,
            controls: this._controlOrder.map(
                (controlId) => this._controls.get(controlId)?.build(),
            ),
        };
    }
}

export class ComponentController implements IController {
    private _component: Component;
    private _cache: CacheController;
    private _props: PropsController;

    constructor(component: Component) {
        this.setComponent(component);
    }

    /**
     * Will replace the component with the new component and update the kept data
     * ! This id will be maintained. If you want to replace everything, you should use the set method
     *
     * @param component - The partial component to replace the current component with
     * @param opts.keep - The keys to keep from the current component
     * @returns the current builder
     */
    replace(component: Component, opts?: { keep?: string[] }) {
        const keptData: Partial<Component> = opts?.keep?.reduce(
            (acc, key) => ({ ...acc, [key]: get(this.build(), key) }),
            {},
        );

        return this.setComponent({
            ...component,
            id: this._component.id,
        }).updateComponentFromPath(keptData ?? {});
    }

    /**
     * A helper method that lets us replace data in the component from a path. It takes in a record of keys that use
     * the path in the component and the value is the information to replace.
     * @param keptData - Record<string, any> - A record of keys which represent the path in
     * the component and the value is the value to repladce it with
     * @returns the current builder
     */
    updateComponentFromPath(keptData: Record<string, any> = {}) {
        this._component = Object.entries(keptData).reduce(
            (acc, [key, value]): Component => {
                return set(acc, key, value);
            },
            this.build(),
        );

        return this;
    }

    setComponent(component: Component): this {
        this._component = component;

        return this.setProps(component.props).setCache(component.cache);
    }

    setCache(cache: any): this {
        this._cache = new CacheController(cache);

        return this;
    }

    getCache(): CacheController {
        return this._cache;
    }

    getProps(): any {
        return this._props.build();
    }

    setProps(props: any = {}): this {
        this._props = new PropsController(props);

        return this;
    }

    updateComponent(component: Partial<Component>): this {
        this._component = { ...this._component, ...component };

        return this;
    }

    updateComponentProps(props: any): this {
        this._props.update({ ...props });

        return this;
    }

    updateControlProps(controlId: string, control: Partial<Control>): this {
        this._props.updateControl(controlId, control);

        return this;
    }

    updateControlByControlId(controlId: string, control: any) {
        this._props.updateControlById(controlId, control);
    }

    build(): Component {
        return {
            ...this._component,
            props: this._props.build(),
            cache: this._cache.build(),
        };
    }
}

export class SectionController implements IController {
    private _section: Section;
    private _components: Map<string, ComponentController> = new Map<
        string,
        ComponentController
    >();
    private _order: string[] = [];
    private _cache: CacheController;

    constructor(section: Section) {
        this.set(section);
    }

    replaceComponent(
        componentId: string,
        component: Component,
        opts: { keep?: string[] },
    ): this {
        this._components.get(componentId)?.replace(component, opts);

        return this;
    }

    set(section: Section): this {
        this._section = section;

        return this.setComponents(section.components).setCache(section.cache);
    }
    update(section: Partial<Section>): this {
        return this.set({ ...this.build(), ...section });
    }

    setCache(cache: any): this {
        this._cache = new CacheController(cache);

        return this;
    }

    getCache(): CacheController {
        return this._cache;
    }

    setComponents(components: Component[]): this {
        this._components = new Map<string, ComponentController>();
        this._order = [...components.map((component) => component.id)];

        components.forEach((component) => {
            this._components.set(
                component.id,
                new ComponentController(component),
            );
        });

        return this;
    }

    getComponents(): Component[] {
        return this._order.map((componentId) => {
            return this._components.get(componentId)?.build();
        });
    }

    getComponent(componentId: string): ComponentController {
        return this._components.get(componentId);
    }

    updateComponent(id: string, component: Partial<Component>): this {
        this._components.get(id)?.updateComponent(component);

        return this;
    }

    updateComponentProps(componentId: string, props: any): this {
        this._components.get(componentId)?.updateComponentProps(props);

        return this;
    }

    updateControlByControlId(controlId: string, control: any) {
        this._components.forEach((component) => {
            component.updateControlByControlId(controlId, control);
        });
    }

    updateControlProps(
        componentId: string,
        controlId: string,
        controlData: Partial<Control>,
    ): this {
        this._components
            .get(componentId)
            ?.updateControlProps(controlId, controlData);

        return this;
    }

    build(): Section {
        return {
            ...this._section,
            components: [...this.getComponents()],
            cache: this._cache.build(),
        };
    }
}

export class CacheController {
    private _cache: any = {};

    constructor(cache: any) {
        this.setCache(cache);
    }

    setCache(cache: any): this {
        this._cache = cache;

        return this;
    }

    updateCache(cache: any): this {
        this._cache = {
            ...this._cache,
            ...cache,
        };

        return this;
    }

    appendDashboardCacheData(key: string, newData: any): this {
        const currentData = get(this._cache, key);

        set(
            this._cache,
            key,
            !isArray(currentData) ? [newData] : [...currentData, newData],
        );

        return this;
    }

    build(): any {
        return this._cache;
    }
}

export class DashboardController implements IController {
    private _dashboard: Dashboard;
    private _cache: CacheController;

    constructor(dashboard: Dashboard) {
        this.setDashboard(dashboard);
    }

    private _sections: Map<string, SectionController> = new Map<
        string,
        SectionController
    >();

    private _sectionOrder: string[] = [];

    setDashboard(dashboard: Dashboard): this {
        this._dashboard = dashboard;

        return this.setSections(dashboard.sections).setCache(dashboard.cache);
    }

    setCache(cache: any): this {
        this._cache = new CacheController(cache);

        return this;
    }

    updateCache(data: any) {
        this._cache.updateCache(data);

        return this;
    }

    getCache(): CacheController {
        return this._cache;
    }

    getSections(): Section[] {
        return (
            this._sectionOrder?.map((sectionId) => {
                return this._sections.get(sectionId)?.build();
            }) ?? []
        );
    }

    updateControlByControlId(controlId: string, control: any): this {
        this._sections.forEach((section) => {
            section.updateControlByControlId(controlId, control);
        });

        return this;
    }

    setSections(sections: Section[]): this {
        this._sections = new Map<string, SectionController>();
        this._sectionOrder = [...sections.map((section) => section.id)];

        sections.forEach((section) => {
            this._sections.set(section.id, new SectionController(section));
        });

        return this;
    }

    getSection(sectionId: string): SectionController {
        return this._sections.get(sectionId);
    }
    updateSection(sectionId: string, section: Partial<Section>): this {
        this._sections.get(sectionId)?.update(section);

        return this;
    }

    updateComponentProps(
        sectionId: string,
        componentId: string,
        props: any,
    ): this {
        this._sections.get(sectionId)?.updateComponentProps(componentId, props);

        return this;
    }

    updateComponent(
        sectionId: string,
        componentId: string,
        component: Partial<Component>,
    ): this {
        this._sections.get(sectionId)?.updateComponent(componentId, component);

        return this;
    }

    updateControlProps(
        sectionId: string,
        componentId: string,
        controlId: string,
        controlData: Partial<Control>,
    ): this {
        this._sections
            .get(sectionId)
            ?.updateControlProps(componentId, controlId, controlData);

        return this;
    }

    appendDashboardData(dashboardId: string, key: string, newData: any): this {
        this._cache.appendDashboardCacheData(key, newData);

        return this;
    }

    replaceComponent(
        sectionId: string,
        componentId: string,
        component: Component,
        opts: { keep?: string[] },
    ): DashboardController {
        this._sections
            .get(sectionId)
            ?.replaceComponent(componentId, component, opts);

        return this;
    }

    build(): Dashboard {
        return {
            ...this._dashboard,
            sections: [...this.getSections()],
            cache: this._cache.build(),
        };
    }
}
